<template>
    <v-dialog v-model="dialog" max-width="900px" persistent scrollable>
        <v-card class="booking-dialog-card">
            <div class="booking-dialog-header pa-5">
                <div class="d-flex align-center justify-space-between">
                    <div class="booking-dialog-title">
                        <v-icon class="mr-2" color="white" size="20">mdi-email-send-outline</v-icon>
                        Send Organiser Itinerary
                    </div>
                    <v-btn icon dark @click="closeDialog()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>
            <v-card-text class="pa-6">
                <div class="booking-section mb-4" v-if="quoteOptions.length > 1">
                    <div class="booking-section-header mb-1">
                        <v-icon size="14" color="#3988AA" class="mr-1">mdi-file-document-outline</v-icon>
                        QUOTE
                    </div>
                    <v-select v-model="selected_quote" :items="quoteOptions" item-text="text" item-value="value"
                        outlined dense hide-details @change="regenerateBody()"></v-select>
                </div>
                <div class="booking-section mb-4">
                    <div class="booking-section-header mb-1">
                        <v-icon size="14" color="#3988AA" class="mr-1">mdi-format-title</v-icon>
                        SUBJECT
                    </div>
                    <v-text-field v-model="subject" outlined dense placeholder="Enter email subject"
                        hide-details></v-text-field>
                </div>
                <div class="booking-section">
                    <div class="booking-section-header mb-2">
                        <v-icon size="14" color="#3988AA" class="mr-1">mdi-text</v-icon>
                        EMAIL CONTENT
                    </div>
                    <wysiwyg v-model="body" placeholder="Compose the itinerary email..." />
                </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-4 custom-buttons">
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog()">Cancel</v-btn>
                <v-btn dark color="#3988AA" :loading="sending" :disabled="!subject || !body" @click="sendEmail()">
                    <v-icon small left>mdi-send</v-icon>
                    Send
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { visibleItineraryRows, getBuildName, DEFAULT_ITINERARY_INTRO } from "@/utils/itineraryRows"
import { buildInfoNoticeItems } from "@/utils/itineraryNotices"
import { dedupeLinkedBuilds } from "@/utils/linkedBuilds"

// Matches the quote builder's wizard step order (CustomQuote.vue's build_index_supplier_types)
// so same-day services appear in the same order the customer built them in. Accommodation
// (type_id 1) is pinned first, since it spans the whole party rather than sitting on one day.
const BUILD_TYPE_ORDER = [1, 2, 6, 8, 4, 5, 3, 7]
const accommodationFirst = (build) => build.type_id === 1 ? 0 : 1

export default {
    props: {
        show: {
            type: Boolean,
            default: false
        },
        lead: {
            type: Object,
            default: () => ({})
        },
        event: {
            type: Object,
            default: () => ({ quotes: [] })
        },
        company_id: {
            type: Number,
            default: null
        }
    },
    data: () => ({
        subject: '',
        body: '',
        sending: false,
        api_url: null,
        selected_quote: null,
    }),
    computed: {
        dialog: {
            get() {
                return this.show
            },
            set(val) {
                if (!val) this.closeDialog()
            }
        },
        quoteOptions() {
            return (this.event?.quotes || [])
                .filter(quote => quote.is_activated)
                .map((quote, index) => ({
                    text: quote.name || `Quote ${index + 1}`,
                    value: quote.id,
                }))
        }
    },
    mounted() {
        this.api_url = process.env.VUE_APP_API_URL
        if (this.show) {
            this.initDialog()
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.sending = false
                this.initDialog()
            }
        }
    },
    methods: {
        closeDialog() {
            this.$emit('close')
        },
        buildDefaultSubject() {
            return `Your Itinerary for ${this.event?.name || ''}`
        },
        getImageUrl(image) {
            if (!image) return ''
            if (image.startsWith('data:')) return image
            return this.api_url + 'media/suppliers/images/' + image
        },
        initDialog() {
            const quotes = this.event?.quotes || []
            const activatedQuotes = quotes.filter(q => q.is_activated == 1)
            const firstActive = activatedQuotes[0] || quotes[0] || null
            this.selected_quote = firstActive ? firstActive.id : null
            this.regenerateBody()
        },
        regenerateBody() {
            this.subject = this.buildDefaultSubject()
            this.body = this.buildDefaultBody()
        },
        getSortedBuilds() {
            const quotes = this.event?.quotes || []
            const source = this.selected_quote
                ? quotes.filter(q => q.id === this.selected_quote)
                : quotes.filter(q => q.is_activated == 1)
            let builds = []
            source.forEach(quote => {
                if (quote.builds) builds = builds.concat(quote.builds)
            })
            // All activated quotes together: a build copied onto a variant quote appears once.
            return dedupeLinkedBuilds(builds).sort((a, b) => {
                const a_first = accommodationFirst(a)
                const b_first = accommodationFirst(b)
                if (a_first !== b_first) return a_first - b_first
                if (a.start_date !== b.start_date) return a.start_date < b.start_date ? -1 : 1
                return BUILD_TYPE_ORDER.indexOf(a.type_id) - BUILD_TYPE_ORDER.indexOf(b.type_id)
            })
        },
        buildRow(label, value) {
            if (!value) return ''
            return `
                <tr>
                    <td style="width:35%;padding:6px 10px;border-bottom:1px solid #D3D3D3;font-family:Montserrat;color:#3988AA;"><b>${label}</b></td>
                    <td style="padding:6px 10px;border-bottom:1px solid #D3D3D3;font-family:Manrope;color:black;"><b>${value}</b></td>
                </tr>`
        },
        renderBuildHtml(build) {
            const isAccom = build.type_id == 1
            const imageUrl = this.getImageUrl(build.supplier?.image || build.option?.image)
            const dateHtml = isAccom
                ? `${this.$date(build.start_date).format('dddd, MMMM D, YYYY')} - ${this.$date(build.end_date).format('dddd, MMMM D, YYYY')}`
                : `${this.$date(build.start_date).format('dddd, MMMM D, YYYY')}`

            let rows = ''
            rows += this.buildRow((build.type?.name || 'Activity') + ':', getBuildName(build))
            if (isAccom) rows += this.buildRow('Accommodation Type:', 'Self Catering Accommodation')
            if (build.venue) rows += this.buildRow('Venue:', build.venue)

            const itineraryRows = visibleItineraryRows(build, { formatDay: (date, fmt) => this.$date(date).format(fmt) })
            itineraryRows.forEach(row => {
                rows += this.buildRow(row.label, row.value)
            })

            if (build.itinerary_notes) rows += this.buildRow('Notes:', build.itinerary_notes)

            return `
                <div style="margin:30px 0;">
                    ${imageUrl ? `<img src="${imageUrl}" style="width:100%;display:block;max-height:260px;object-fit:cover;" />` : ''}
                    <div style="font-family:Arial;font-size:28px;font-weight:600;color:#3988AA;text-align:center;text-transform:uppercase;margin:10px 0;">
                        ${build.type?.name || 'Activity'}
                    </div>
                    <div style="font-family:Manrope;text-transform:uppercase;text-align:center;color:black;margin-bottom:10px;">
                        ${dateHtml}
                    </div>
                    <table style="width:100%;border-collapse:collapse;">
                        ${rows}
                    </table>
                </div>`
        },
        buildInfoNoticeHtml() {
            const items = buildInfoNoticeItems(this.event.company)

            const cell = (item) => `
                <div style="display:inline-block;vertical-align:top;width:190px;max-width:47%;padding:25px 20px;box-sizing:border-box;text-align:center;font-family:Manrope;">
                    <div style="font-size:26px;margin-bottom:8px;">${item.icon}</div>
                    <div style="font-weight:700;color:black;font-size:14px;margin-bottom:6px;">${item.title}</div>
                    <div style="color:#4D4D4D;font-size:12px;line-height:1.5;">${item.text}</div>
                </div>`

            return `
                <div style="background:#EFEFEF;padding:20px 10px;margin-top:20px;font-family:Manrope;">
                    <div style="text-align:center;font-size:13px;color:black;margin-bottom:10px;">
                        <b>PLEASE READ CAREFULLY</b> Check events and timings above. If we do not hear from you within 3 days then we will consider the itinerary final.
                    </div>
                    <div style="text-align:center;font-size:0;">
                        ${items.map(cell).join('')}
                    </div>
                </div>`
        },
        buildDefaultBody() {
            const lead = this.lead || {}
            const builds = this.getSortedBuilds()
            const buildsHtml = builds.map(build => this.renderBuildHtml(build)).join('')

            return `
                <style>
                    .email-container { width: 100%; max-width: 850px; margin: 0 auto; }
                    @media only screen and (max-width: 650px) {
                        .email-container { max-width: 100% !important; }
                    }
                </style>
                <div class="email-container">
                <div style="text-align:center;font-size:32px;line-height:1.2;font-weight:600;color:#3988AA;">IT'S GONNA BE FUN</div>
                <div style="font-size:24px;font-weight:600;text-transform:uppercase;color:#FAAF3A;text-align:center;font-family:sans-serif;">
                    ${lead.first_name || ''}'s ${lead.activity_type || ''} Itinerary
                </div>
                <div style="font-size:14px;font-weight:500;text-align:center;text-transform:uppercase;font-family:Manrope;margin-bottom:20px;">
                    ${lead.start_date ? this.$date(lead.start_date).format('MMMM D, YYYY') : ''} TO ${lead.end_date ? this.$date(lead.end_date).format('MMMM D, YYYY') : ''}
                </div>
                <table style="width:100%;table-layout:fixed;text-align:center;font-family:Manrope;font-size:12px;margin-bottom:20px;">
                    <tr>
                        <td style="padding:6px 4px;vertical-align:top;word-break:break-word;color:black;"><small>GROUP LEADER</small><br><b style="text-transform:uppercase;color:black;">${lead.first_name || ''} ${lead.last_name || ''}</b></td>
                        <td style="padding:6px 4px;vertical-align:top;word-break:break-word;color:black;"><small>CONTACT</small><br><b style="text-transform:uppercase;color:black;">${lead.phone || lead.email || ''}</b></td>
                        <td style="padding:6px 4px;vertical-align:top;word-break:break-word;color:black;"><small>ARRIVAL</small><br><b style="text-transform:uppercase;color:black;">${lead.start_date ? this.$date(lead.start_date).format('MMMM D, YYYY') : ''}</b></td>
                        <td style="padding:6px 4px;vertical-align:top;word-break:break-word;color:black;"><small>DEPARTURE</small><br><b style="text-transform:uppercase;color:black;">${lead.end_date ? this.$date(lead.end_date).format('MMMM D, YYYY') : ''}</b></td>
                    </tr>
                </table>
                <p style="text-align:left;color:black;font-size:16px;">
                    Dear ${lead.first_name || ''} ${lead.last_name || ''},
                    <br><br>
                    ${(this.event?.itinerary_intro || DEFAULT_ITINERARY_INTRO).replace(/\n/g, '<br>')}
                </p>
                ${buildsHtml}
                ${this.buildInfoNoticeHtml()}
                </div>
            `
        },
        async sendEmail() {
            this.sending = true
            try {
                const payload = {
                    company_id: this.company_id,
                    lead: this.lead,
                    event: this.event,
                    subject: this.subject,
                    body: this.body,
                    email_type: 'organiser_itinerary',
                }
                const result = await (await this.$axios.post('admin/events/send_organiser_email', payload)).data
                if (result.response == true) {
                    this.$toast.success(result.message)
                    this.closeDialog()
                } else {
                    this.$toast.error('Failed to send itinerary email')
                }
            } catch (error) {
                this.$toast.error('Failed to send itinerary email')
            } finally {
                this.sending = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.booking-dialog-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.booking-dialog-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
}

.booking-section-header {
    font-family: Manrope;
    font-size: 12px;
    font-weight: 700;
    color: #3D3D3D;
    display: flex;
    align-items: center;
}
</style>
